/**
 * BattleScene.ts - Combat rendering with FF1-style presentation
 * Handles enemy sprites, party status, battle menu, attack animations, damage numbers, spell casting
 */

import {
  Scene,
  GameInput,
  GameKey,
  BattleState,
  BattlePhase,
  BattleEnemy,
  BattleAnimation,
  MenuItem,
} from './types/engine';
import { Renderer } from './Renderer';
import { SpriteManager } from './SpriteManager';
import { getEnemyTemplate } from './EncounterSystem';
import { levelSystem } from './LevelSystem';
import { Character, LevelUpResult } from './types/character';
import {
  SpellSystem,
  Character as SpellCharacter,
  Enemy as SpellEnemy,
} from './SpellSystem';
import {
  SpellDefinition,
  SpellTarget,
  StatusEffect,
  CharacterClass,
} from './data/SpellData';
import { BattleEffectsManager, EffectType } from './effects/BattleEffects';
import { BattlePose } from './BattleCharacterSprites';
import { ItemDefinition, getItem } from './data/ItemData';
import { ItemType } from '../lib/types/game';

export interface BattleResult {
  victory: boolean;
  fled: boolean;
  expGained: number;
  pasGained: number; // PAS tokens earned
  enemiesDefeated: BattleEnemy[];
  levelUps?: Map<number, LevelUpResult[]>; // Character index -> level up results
  updatedParty: PartyMember[]; // Party state after battle
  updatedInventory: Map<number, number>; // Inventory after item usage
}

export interface PartyMember {
  id: number;
  name: string;
  class: CharacterClass;
  level: number;
  hp: number;
  maxHP: number;
  mp: number;
  maxMP: number;
  intelligence: number;
  spirit: number;
  status: string;
  experience?: number;
  learnedSpells: number[]; // Spell IDs this character knows
  spellCharges: Map<number, number>; // Spell charges
}

export class BattleScene implements Scene {
  private renderer: Renderer;
  private spriteManager: SpriteManager;
  private effectsManager: BattleEffectsManager;

  private state: BattleState;
  private party: PartyMember[];
  private selectedPartyMember: number = 0;
  private selectedTarget: number = 0;
  private selectedAction: string = '';
  private menuIndex: number = 0;

  // Magic menu state
  private magicMenuActive: boolean = false;
  private magicLevelIndex: number = 0; // Which spell level (1-8)
  private magicSpellIndex: number = 0; // Which spell in that level
  private selectedSpell: SpellDefinition | null = null;

  // Item menu state
  private itemMenuActive: boolean = false;
  private itemMenuIndex: number = 0; // Current selection in item list
  private selectedItem: ItemDefinition | null = null;
  private partyInventory: Map<number, number> = new Map(); // itemId -> quantity

  // Animation tracking
  private flashTimer: number = 0;
  private shakeOffset: { x: number; y: number } = { x: 0, y: 0 };
  private damageNumbers: Array<{
    value: number;
    x: number;
    y: number;
    elapsed: number;
    duration: number;
    isCrit: boolean;
  }> = [];

  // Battle menu
  private battleMenuItems: MenuItem[] = [
    { label: 'FIGHT', enabled: true, value: 'fight' },
    { label: 'MAGIC', enabled: true, value: 'magic' },
    { label: 'ITEM', enabled: true, value: 'item' },
    { label: 'RUN', enabled: true, value: 'run' },
  ];

  // Intro animation
  private introTimer: number = 0;
  private readonly INTRO_DURATION: number = 1000;

  // Victory/defeat state
  private resultTimer: number = 0;
  private readonly RESULT_DURATION: number = 2500; // Slightly longer to read results
  private expGained: number = 0;
  private pasGained: number = 0; // PAS tokens earned
  private battleEnded: boolean = false; // Prevent duplicate callbacks

  // Callback for when battle ends
  private onBattleEndCallback: ((result: BattleResult) => void) | null = null;

  // Level up tracking
  private levelUps: Map<number, LevelUpResult[]> = new Map();
  private showingLevelUps: boolean = false;

  // Encounter zone info
  private encounterZone: string = '';

  // Battle message for player feedback
  private battleMessage: string = '';
  private messageTimer: number = 0;
  private readonly MESSAGE_DURATION: number = 1500;

  // Current turn indicator
  private isEnemyTurn: boolean = false;

  // Character battle poses for animation
  private characterPoses: Map<number, BattlePose> = new Map();

  constructor(
    ctx: CanvasRenderingContext2D,
    spriteManager: SpriteManager,
    party: PartyMember[]
  ) {
    this.renderer = new Renderer(ctx);
    this.spriteManager = spriteManager;
    this.effectsManager = new BattleEffectsManager(spriteManager);
    this.party = party;

    this.state = {
      phase: BattlePhase.INTRO,
      currentTurn: 0,
      partyHP: party.map((p) => p.hp),
      enemyHP: [],
      enemies: [],
      turnQueue: [],
      animations: [],
    };
  }

  // =========================================================================
  // Scene Interface
  // =========================================================================

  init(): void {
    this.state.phase = BattlePhase.INTRO;
    this.selectedPartyMember = 0;
    this.selectedTarget = 0;
    this.menuIndex = 0;
    this.introTimer = 0;
    this.resultTimer = 0;
    this.expGained = 0;
    this.pasGained = 0;
    this.levelUps = new Map();
    this.showingLevelUps = false;
    this.magicMenuActive = false;
    this.selectedSpell = null;
    this.battleEnded = false;
    this.battleMessage = '';
    this.messageTimer = 0;
    this.isEnemyTurn = false;
    // Initialize all characters to idle pose
    this.characterPoses.clear();
    this.party.forEach((_, index) => {
      this.characterPoses.set(index, BattlePose.IDLE);
    });
  }

  update(deltaTime: number): void {
    // Update effects manager (handles all visual effects)
    this.effectsManager.update(deltaTime);

    // Update animations
    this.updateAnimations(deltaTime);

    // Update damage numbers
    this.updateDamageNumbers(deltaTime);

    // Update flash/shake effects
    if (this.flashTimer > 0) {
      this.flashTimer -= deltaTime;
    }

    // Update battle message timer
    if (this.messageTimer > 0) {
      this.messageTimer -= deltaTime;
      if (this.messageTimer <= 0) {
        this.battleMessage = '';
      }
    }

    // Phase-specific updates
    switch (this.state.phase) {
      case BattlePhase.INTRO:
        this.introTimer += deltaTime;
        if (this.introTimer >= this.INTRO_DURATION) {
          this.state.phase = BattlePhase.MENU;
        }
        break;

      case BattlePhase.ANIMATING:
        // Wait for animations to complete (including effects)
        if (
          !this.hasActiveAnimations() &&
          this.damageNumbers.length === 0 &&
          !this.effectsManager.hasActiveEffects()
        ) {
          this.nextTurn();
        }
        break;

      case BattlePhase.VICTORY:
        this.resultTimer += deltaTime;
        // Auto-exit after result display (or showing level ups)
        if (this.resultTimer >= this.RESULT_DURATION && !this.showingLevelUps && !this.battleEnded) {
          this.battleEnded = true; // Prevent duplicate callbacks
          if (this.onBattleEndCallback) {
            const result: BattleResult = {
              victory: true,
              fled: false,
              expGained: this.expGained,
              pasGained: this.pasGained,
              enemiesDefeated: this.state.enemies.filter((e) => e.dead),
              levelUps: this.levelUps.size > 0 ? this.levelUps : undefined,
              updatedParty: [...this.party],
              updatedInventory: new Map(this.partyInventory),
            };
            console.log('[BattleScene] Triggering victory callback');
            this.onBattleEndCallback(result);
          }
        }
        break;

      case BattlePhase.DEFEAT:
        this.resultTimer += deltaTime;
        if (this.resultTimer >= this.RESULT_DURATION && !this.battleEnded) {
          this.battleEnded = true; // Prevent duplicate callbacks
          if (this.onBattleEndCallback) {
            const result: BattleResult = {
              victory: false,
              fled: false,
              expGained: 0,
              pasGained: 0,
              enemiesDefeated: [],
              updatedParty: [...this.party],
              updatedInventory: new Map(this.partyInventory),
            };
            console.log('[BattleScene] Triggering defeat callback');
            this.onBattleEndCallback(result);
          }
        }
        break;
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    this.renderer.setContext(ctx);

    // Apply screen shake (from effects manager)
    const shakeOffset = this.effectsManager.getScreenShakeOffset();
    ctx.save();
    ctx.translate(this.shakeOffset.x + shakeOffset.x, this.shakeOffset.y + shakeOffset.y);

    // Render background (black with slight gradient)
    this.renderBackground(ctx);

    // Render enemies
    this.renderEnemies(ctx);

    // Render party character sprites (right side of screen)
    this.renderPartySprites(ctx);

    // Render party status window
    this.renderPartyStatus(ctx);

    // Render battle menu (if in menu phase)
    if (this.state.phase === BattlePhase.MENU && !this.magicMenuActive && !this.itemMenuActive) {
      this.renderBattleMenu(ctx);
    }

    // Render magic menu (if active)
    if (this.magicMenuActive) {
      this.renderMagicMenu(ctx);
    }

    // Render item menu (if active)
    if (this.itemMenuActive) {
      this.renderItemMenu(ctx);
    }

    // Render targeting cursor
    if (this.state.phase === BattlePhase.TARGETING) {
      this.renderTargetCursor(ctx);
    }

    // Render damage numbers
    this.renderDamageNumbers(ctx);

    // Render battle effects (particles, spells, etc.)
    this.effectsManager.render(this.renderer, ctx);

    // Render victory/defeat results
    if (this.state.phase === BattlePhase.VICTORY || this.state.phase === BattlePhase.DEFEAT) {
      this.renderBattleResult(ctx);
    }

    // Render battle message (action feedback)
    if (this.battleMessage && this.messageTimer > 0) {
      this.renderBattleMessage(ctx);
    }

    // Render turn indicator
    this.renderTurnIndicator(ctx);

    // Flash effect
    if (this.flashTimer > 0) {
      const alpha = (this.flashTimer / 100) * 0.5;
      ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
      ctx.fillRect(0, 0, 256, 240);
    }

    ctx.restore();
  }

  /**
   * Render battle message box (shows action text)
   */
  private renderBattleMessage(_ctx: CanvasRenderingContext2D): void {
    const msgX = 104;
    const msgY = 8;
    const msgWidth = 144;
    const msgHeight = 24;

    // Semi-transparent background
    this.renderer.drawWindow(msgX, msgY, msgWidth, msgHeight);

    // Message text
    this.renderer.drawText(this.battleMessage, msgX + 8, msgY + 8, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffffff',
    });
  }

  /**
   * Render turn indicator
   */
  private renderTurnIndicator(_ctx: CanvasRenderingContext2D): void {
    if (this.state.phase !== BattlePhase.MENU && this.state.phase !== BattlePhase.TARGETING) {
      return;
    }

    const indicatorX = 8;
    const indicatorY = 64;

    // Show whose turn it is
    if (this.isEnemyTurn) {
      this.renderer.drawText('ENEMY TURN', indicatorX, indicatorY, {
        font: 'ff1',
        fontSize: 8,
        color: '#ff6666',
      });
    } else {
      const currentMember = this.party[this.selectedPartyMember];
      const turnText = currentMember ? `${currentMember.name}'s turn` : 'Your turn';
      this.renderer.drawText(turnText, indicatorX, indicatorY, {
        font: 'ff1',
        fontSize: 8,
        color: '#66ff66',
      });
    }
  }

  handleInput(input: GameInput): void {
    if (this.magicMenuActive) {
      this.handleMagicMenuInput(input);
    } else if (this.itemMenuActive) {
      this.handleItemMenuInput(input);
    } else if (this.state.phase === BattlePhase.MENU) {
      this.handleMenuInput(input);
    } else if (this.state.phase === BattlePhase.TARGETING) {
      this.handleTargetingInput(input);
    }
  }

  cleanup(): void {
    this.damageNumbers = [];
    this.state.animations = [];
    this.effectsManager.clear();
  }

  // =========================================================================
  // Rendering
  // =========================================================================

  private renderBackground(ctx: CanvasRenderingContext2D): void {
    // Simple black background (FF1 style)
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, 256, 240);

    // Optional: subtle gradient for depth
    const gradient = ctx.createLinearGradient(0, 0, 0, 240);
    gradient.addColorStop(0, 'rgba(0, 0, 64, 0.3)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 240);
  }

  private renderEnemies(ctx: CanvasRenderingContext2D): void {
    this.state.enemies.forEach((enemy, index) => {
      if (enemy.dead) return;

      // Draw enemy sprite using enhanced sprite system
      // Try to get by enemy ID first (1-43), fall back to spriteId string
      const sprite =
        this.spriteManager.getEnemySprite(enemy.id) || this.spriteManager.getSprite(enemy.spriteId);

      if (sprite) {
        ctx.save();
        // Fade based on HP percentage
        const hpPercent = enemy.hp / enemy.maxHP;
        ctx.globalAlpha = 0.5 + hpPercent * 0.5; // 50% to 100% opacity
        ctx.drawImage(
          sprite.image,
          sprite.x,
          sprite.y,
          sprite.width,
          sprite.height,
          enemy.x,
          enemy.y,
          sprite.width,
          sprite.height
        );
        ctx.restore();
      } else {
        // Placeholder if sprite not loaded - draw a colored rectangle
        ctx.fillStyle = '#880000';
        ctx.fillRect(enemy.x, enemy.y, 32, 32);
        ctx.strokeStyle = '#ff0000';
        ctx.strokeRect(enemy.x, enemy.y, 32, 32);
      }

      // Enemy name above sprite (shortened if needed)
      const displayName = enemy.name.length > 8 ? enemy.name.substring(0, 7) + '.' : enemy.name;
      this.renderer.drawText(displayName, enemy.x, enemy.y - 16, {
        font: 'ff1',
        fontSize: 6,
        color: '#ffcc00',
      });

      // HP bar below enemy name
      const barWidth = 32;
      const barHeight = 4;
      const barX = enemy.x;
      const barY = enemy.y - 10;

      // Background
      ctx.fillStyle = '#222222';
      ctx.fillRect(barX, barY, barWidth, barHeight);

      // HP fill with color gradient based on health
      const hpWidth = (enemy.hp / enemy.maxHP) * barWidth;
      const hpPercent = enemy.hp / enemy.maxHP;
      if (hpPercent > 0.5) {
        ctx.fillStyle = '#00cc00'; // Green
      } else if (hpPercent > 0.25) {
        ctx.fillStyle = '#cccc00'; // Yellow
      } else {
        ctx.fillStyle = '#cc0000'; // Red
      }
      ctx.fillRect(barX, barY, hpWidth, barHeight);

      // Border
      ctx.strokeStyle = '#666666';
      ctx.lineWidth = 1;
      ctx.strokeRect(barX, barY, barWidth, barHeight);

      // Targeting highlight if this enemy is selected
      if (this.state.phase === BattlePhase.TARGETING && index === this.selectedTarget) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.strokeRect(enemy.x - 2, enemy.y - 2, 36, 36);
      }
    });
  }

  /**
   * Render party character sprites at the bottom of the battle screen
   * FF1-style layout: characters in a horizontal row, facing up toward enemies
   * Uses 32x32 battle sprites with poses (IDLE, ATTACK, CAST, HURT, VICTORY, DEAD)
   */
  private renderPartySprites(ctx: CanvasRenderingContext2D): void {
    // Party sprites positioned at bottom, in horizontal row
    // Canvas is 256x240, party status window is at y=160
    const spriteSize = 32; // Battle sprites are 32x32
    const partyCount = this.party.length;
    const totalWidth = partyCount * spriteSize + (partyCount - 1) * 8; // sprites + gaps
    const startX = (256 - totalWidth) / 2; // Center horizontally
    const startY = 115; // Above the party status window (which starts at y=160)

    this.party.forEach((member, index) => {
      const x = startX + index * (spriteSize + 8);
      const y = startY;

      // Check if character is dead
      const isDead = member.hp <= 0 || member.status === 'DEAD';

      // Determine the pose based on state
      let pose = this.characterPoses.get(index) ?? BattlePose.IDLE;
      if (isDead) {
        pose = BattlePose.DEAD;
      } else if (this.state.phase === BattlePhase.VICTORY) {
        pose = BattlePose.VICTORY;
      }

      // Get character battle sprite based on class and pose
      const className = this.getClassSpriteName(member.class);
      const sprite = this.spriteManager.getBattleCharacterSprite(className, pose);

      if (sprite) {
        ctx.save();

        // Grey out dead characters (in addition to using DEAD pose)
        if (isDead) {
          ctx.globalAlpha = 0.5;
        }

        // Highlight active character with a glow
        const isActive = this.state.phase === BattlePhase.MENU &&
                        index === this.selectedPartyMember &&
                        !this.isEnemyTurn;
        if (isActive && !isDead) {
          // Draw glow behind sprite
          ctx.shadowColor = '#66ff66';
          ctx.shadowBlur = 8;
        }

        // Step forward animation when attacking (move UP toward enemies)
        const stepOffset = this.effectsManager.getCharacterStepOffset(index);
        const drawY = y - stepOffset; // Move up toward enemies

        // Draw sprite (already 32x32, no scaling needed)
        ctx.drawImage(
          sprite.image,
          sprite.x,
          sprite.y,
          sprite.width,
          sprite.height,
          x,
          drawY,
          spriteSize,
          spriteSize
        );

        ctx.restore();

        // Draw selection indicator for active character
        if (isActive && !isDead) {
          ctx.strokeStyle = '#66ff66';
          ctx.lineWidth = 2;
          ctx.strokeRect(x - 2, drawY - 2, spriteSize + 4, spriteSize + 4);
        }

        // Draw targeting cursor when party member is being targeted
        if (this.state.phase === BattlePhase.TARGETING && this.isTargetingParty()) {
          // For healing/buff spells targeting party members
          if (index === this.selectedTarget) {
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 2;
            ctx.strokeRect(x - 2, drawY - 2, spriteSize + 4, spriteSize + 4);
          }
        }
      } else {
        // Fallback colored rectangle if sprite not available
        ctx.fillStyle = isDead ? '#333333' : this.getClassColor(member.class);
        ctx.fillRect(x, y, spriteSize, spriteSize);
        // Draw class abbreviation on fallback
        ctx.fillStyle = '#ffffff';
        ctx.font = '8px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(this.getClassAbbrev(member.class), x + spriteSize/2, y + spriteSize/2 + 3);
      }
    });
  }

  /**
   * Map CharacterClass enum to sprite name string
   * Must match ASSET_MANIFEST.characterClassRows keys (with underscores)
   */
  private getClassSpriteName(charClass: CharacterClass): string {
    const classNames: Record<CharacterClass, string> = {
      [CharacterClass.Fighter]: 'FIGHTER',
      [CharacterClass.Thief]: 'THIEF',
      [CharacterClass.Monk]: 'MONK',
      [CharacterClass.RedMage]: 'RED_MAGE',
      [CharacterClass.WhiteMage]: 'WHITE_MAGE',
      [CharacterClass.BlackMage]: 'BLACK_MAGE',
    };
    return classNames[charClass] || 'FIGHTER';
  }

  /**
   * Get a color for the class (fallback when sprite not available)
   */
  private getClassColor(charClass: CharacterClass): string {
    const classColors: Record<CharacterClass, string> = {
      [CharacterClass.Fighter]: '#ff4444',
      [CharacterClass.Thief]: '#44aa44',
      [CharacterClass.Monk]: '#ffaa00',
      [CharacterClass.RedMage]: '#ff4488',
      [CharacterClass.WhiteMage]: '#ffffff',
      [CharacterClass.BlackMage]: '#4444ff',
    };
    return classColors[charClass] || '#888888';
  }

  /**
   * Check if currently targeting party members (for heal/buff spells)
   */
  private isTargetingParty(): boolean {
    if (this.selectedSpell) {
      // Healing/buff spells target party
      return this.selectedSpell.target === SpellTarget.SingleAlly ||
             this.selectedSpell.target === SpellTarget.AllAllies ||
             this.selectedSpell.target === SpellTarget.Self;
    }
    if (this.selectedItem) {
      // Consumables that heal/revive target party members
      // Check by item type - consumables typically target party
      return this.selectedItem.type === ItemType.CONSUMABLE;
    }
    return false;
  }

  private renderPartyStatus(_ctx: CanvasRenderingContext2D): void {
    const windowX = 8;
    const windowY = 160;
    const windowWidth = 240;
    const windowHeight = 76;

    // Draw window
    this.renderer.drawWindow(windowX, windowY, windowWidth, windowHeight);

    // Draw each party member
    this.party.forEach((member, index) => {
      const x = windowX + 6 + index * 58;
      const y = windowY + 6;

      // Highlight active character during their turn
      const isActive = this.state.phase === BattlePhase.MENU &&
                       index === this.selectedPartyMember &&
                       !this.isEnemyTurn;

      // Draw selection box for active character (outline only)
      if (isActive) {
        this.renderer.drawRect(x - 2, y - 2, 56, 68, '#66ff66', false);
      }

      // Character is dead - grey out
      const isDead = member.hp <= 0 || member.status === 'DEAD';

      // Name (with color indicating status)
      let nameColor = '#ffffff';
      if (isDead) {
        nameColor = '#666666';
      } else if (isActive) {
        nameColor = '#66ff66';
      }

      this.renderer.drawText(member.name, x, y, {
        font: 'ff1',
        fontSize: 8,
        color: nameColor,
      });

      // Class abbreviation (smaller)
      const classAbbrev = this.getClassAbbrev(member.class);
      this.renderer.drawText(classAbbrev, x + 40, y, {
        font: 'ff1',
        fontSize: 6,
        color: '#888888',
      });

      // HP bar
      const hpBarColor = isDead ? '#333333' : undefined;
      this.renderer.drawHPBar(x, y + 14, 52, 6, member.hp, member.maxHP, hpBarColor);

      // HP text
      const hpText = `${Math.max(0, member.hp)}/${member.maxHP}`;
      this.renderer.drawText(hpText, x, y + 24, {
        font: 'ff1',
        fontSize: 7,
        color: isDead ? '#555555' : '#cccccc',
      });

      // MP bar (if character has MP)
      if (member.maxMP > 0) {
        this.renderer.drawMPBar(x, y + 34, 52, 6, member.mp, member.maxMP);
        const mpText = `${member.mp}/${member.maxMP}`;
        this.renderer.drawText(mpText, x, y + 44, {
          font: 'ff1',
          fontSize: 7,
          color: isDead ? '#555555' : '#8888ff',
        });
      }

      // Status effect (if any, below MP)
      const statusY = member.maxMP > 0 ? y + 54 : y + 34;
      if (member.status && member.status !== 'OK' && member.status !== 'NORMAL') {
        this.renderer.drawText(member.status, x, statusY, {
          font: 'ff1',
          fontSize: 6,
          color: isDead ? '#444444' : '#ff6666',
        });
      } else if (isDead) {
        this.renderer.drawText('DEAD', x, statusY, {
          font: 'ff1',
          fontSize: 6,
          color: '#ff0000',
        });
      }
    });
  }

  /**
   * Get abbreviated class name
   */
  private getClassAbbrev(classType: CharacterClass | number): string {
    // Handle both enum values and numeric values (for upgraded classes)
    const classNum = typeof classType === 'number' ? classType : classType;
    switch (classNum) {
      case 0: return 'FI'; // Fighter
      case 1: return 'TH'; // Thief
      case 2: return 'MO'; // Monk
      case 3: return 'RM'; // Red Mage
      case 4: return 'WM'; // White Mage
      case 5: return 'BM'; // Black Mage
      case 6: return 'KN'; // Knight (upgraded Fighter)
      case 7: return 'NI'; // Ninja (upgraded Thief)
      case 8: return 'MA'; // Master (upgraded Monk)
      case 9: return 'RW'; // Red Wizard
      case 10: return 'WW'; // White Wizard
      case 11: return 'BW'; // Black Wizard
      default: return '??';
    }
  }

  private renderBattleMenu(_ctx: CanvasRenderingContext2D): void {
    const menuX = 8;
    const menuY = 80;
    const menuWidth = 88;
    const menuHeight = 72;

    // Draw menu window
    this.renderer.drawWindow(menuX, menuY, menuWidth, menuHeight);

    // Draw menu items
    this.battleMenuItems.forEach((item, index) => {
      const itemY = menuY + 8 + index * 16;
      const isSelected = index === this.menuIndex;

      // Draw cursor
      if (isSelected) {
        this.renderer.drawCursor(menuX + 4, itemY + 2);
      }

      // Draw item text
      const textColor = item.enabled ? '#ffffff' : '#808080';
      this.renderer.drawText(item.label, menuX + 16, itemY, {
        font: 'ff1',
        fontSize: 8,
        color: textColor,
      });
    });
  }

  private renderMagicMenu(_ctx: CanvasRenderingContext2D): void {
    const menuX = 104;
    const menuY = 80;
    const menuWidth = 144;
    const menuHeight = 152;

    // Draw magic menu window
    this.renderer.drawWindow(menuX, menuY, menuWidth, menuHeight);

    const character = this.party[this.selectedPartyMember];
    if (!character) return;

    // Title
    this.renderer.drawText('MAGIC', menuX + menuWidth / 2, menuY + 8, {
      font: 'ff1',
      fontSize: 10,
      color: '#ffff00',
      align: 'center',
    });

    // Get spells organized by level
    const spellsByLevel = SpellSystem.getSpellsByLevel(character as any, character.learnedSpells);

    // Draw spell levels (1-8)
    let yOffset = menuY + 24;
    const levels = Array.from(spellsByLevel.keys()).sort();

    if (levels.length === 0) {
      this.renderer.drawText('No spells learned', menuX + 8, yOffset, {
        font: 'ff1',
        fontSize: 8,
        color: '#808080',
      });
      return;
    }

    // Show current level and its spells
    const currentLevel = levels[this.magicLevelIndex] || 1;
    const spells = spellsByLevel.get(currentLevel) || [];

    // Level indicator
    this.renderer.drawText(`Level ${currentLevel}`, menuX + 8, yOffset, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffffff',
    });

    // Navigation hint
    this.renderer.drawText('< >', menuX + menuWidth - 24, yOffset, {
      font: 'ff1',
      fontSize: 8,
      color: '#aaaaaa',
    });

    yOffset += 16;

    // Draw spells in current level
    spells.forEach((spell, index) => {
      const isSelected = index === this.magicSpellIndex;
      const charges = SpellSystem.getRemainingCharges(character as any, spell.id);
      const maxCharges = SpellSystem.getMaxCharges(spell.id);

      // Draw cursor
      if (isSelected) {
        this.renderer.drawCursor(menuX + 4, yOffset + 2);
      }

      // Draw spell name
      const canCast = charges > 0 && !character.status.includes('SILENCE');
      const textColor = canCast ? '#ffffff' : '#808080';

      this.renderer.drawText(spell.name, menuX + 16, yOffset, {
        font: 'ff1',
        fontSize: 8,
        color: textColor,
      });

      // Draw charges
      this.renderer.drawText(`${charges}/${maxCharges}`, menuX + menuWidth - 40, yOffset, {
        font: 'ff1',
        fontSize: 8,
        color: charges > 0 ? '#00ff00' : '#ff0000',
      });

      yOffset += 12;
    });

    // Spell description (if selected)
    if (spells[this.magicSpellIndex]) {
      const spell = spells[this.magicSpellIndex];
      const descY = menuY + menuHeight - 32;

      if (spell?.description) {
        this.renderer.drawText(spell.description, menuX + 8, descY, {
          font: 'ff1',
          fontSize: 7,
          color: '#aaaaaa',
        });
      }
    }
  }

  private renderTargetCursor(_ctx: CanvasRenderingContext2D): void {
    // Item targeting - always targets party members
    if (this.selectedItem) {
      const member = this.party[this.selectedTarget];
      if (!member) return;

      const windowX = 8;
      const windowY = 160;
      const x = windowX + 8 + this.selectedTarget * 58;
      const bounce = Math.sin(Date.now() / 200) * 2;

      // Draw cursor above party member
      this.renderer.drawText('v', x + 20, windowY - 8 + bounce, {
        font: 'ff1',
        fontSize: 12,
        color: '#00ffff', // Cyan for item targeting
      });

      // Show item name being used
      this.renderer.drawText(this.selectedItem.name, 128, windowY - 20, {
        font: 'ff1',
        fontSize: 8,
        color: '#00ffff',
        align: 'center',
      });
      return;
    }

    if (this.selectedSpell) {
      // Spell targeting (could be enemies or allies)
      if (
        this.selectedSpell.target === SpellTarget.SingleEnemy ||
        this.selectedSpell.target === SpellTarget.AllEnemies
      ) {
        const enemy = this.state.enemies[this.selectedTarget];
        if (!enemy || enemy.dead) return;

        const cursorX = enemy.x - 16;
        const cursorY = enemy.y + 16;
        const bounce = Math.sin(Date.now() / 200) * 2;

        this.renderer.drawText('>', cursorX, cursorY + bounce, {
          font: 'ff1',
          fontSize: 12,
          color: '#ffffff',
        });
      } else if (
        this.selectedSpell.target === SpellTarget.SingleAlly ||
        this.selectedSpell.target === SpellTarget.Self
      ) {
        // Show cursor next to party member
        const member = this.party[this.selectedTarget];
        if (!member) return;

        const windowX = 8;
        const windowY = 160;
        const x = windowX + 8 + this.selectedTarget * 60;
        const bounce = Math.sin(Date.now() / 200) * 2;

        this.renderer.drawText('<', x - 8, windowY + 20 + bounce, {
          font: 'ff1',
          fontSize: 12,
          color: '#ffffff',
        });
      }
    } else {
      // Normal attack targeting
      const enemy = this.state.enemies[this.selectedTarget];
      if (!enemy || enemy.dead) return;

      const cursorX = enemy.x - 16;
      const cursorY = enemy.y + 16;
      const bounce = Math.sin(Date.now() / 200) * 2;

      this.renderer.drawText('>', cursorX, cursorY + bounce, {
        font: 'ff1',
        fontSize: 12,
        color: '#ffffff',
      });
    }
  }

  private renderBattleResult(_ctx: CanvasRenderingContext2D): void {
    const windowX = 48;
    const windowY = 60;
    const windowWidth = 160;
    const windowHeight = 120;
    const padding = 12;

    // Draw result window
    this.renderer.drawWindow(windowX, windowY, windowWidth, windowHeight);

    if (this.state.phase === BattlePhase.VICTORY) {
      // Victory message - centered
      this.renderer.drawText('VICTORY!', windowX + windowWidth / 2, windowY + 16, {
        font: 'ff1',
        fontSize: 12,
        color: '#ffff00',
        align: 'center',
        shadow: true,
      });

      // Experience and PAS on same row for compact display
      this.renderer.drawText(`EXP: ${this.expGained}`, windowX + padding, windowY + 40, {
        font: 'ff1',
        fontSize: 9,
        color: '#ffffff',
        shadow: true,
      });

      this.renderer.drawText(`PAS: ${this.pasGained}`, windowX + padding + 80, windowY + 40, {
        font: 'ff1',
        fontSize: 9,
        color: '#ffd700',
        shadow: true,
      });

      // Separator line
      let yOffset = windowY + 58;

      // Level ups
      if (this.levelUps.size > 0) {
        this.renderer.drawText('LEVEL UP!', windowX + windowWidth / 2, yOffset, {
          font: 'ff1',
          fontSize: 8,
          color: '#00ff00',
          align: 'center',
        });
        yOffset += 14;

        this.levelUps.forEach((results, charIndex) => {
          const member = this.party[charIndex];
          if (member && results.length > 0) {
            const finalLevel = results[results.length - 1]?.newLevel;
            if (finalLevel !== undefined && yOffset < windowY + windowHeight - 12) {
              // Truncate name if too long
              const name = member.name.length > 8 ? member.name.substring(0, 7) + '.' : member.name;
              this.renderer.drawText(`${name} Lv${finalLevel}`, windowX + padding, yOffset, {
                font: 'ff1',
                fontSize: 8,
                color: '#00ff00',
                shadow: true,
              });
              yOffset += 12;
            }
          }
        });
      } else {
        // Enemies defeated (if no level ups)
        this.renderer.drawText('Defeated:', windowX + padding, yOffset, {
          font: 'ff1',
          fontSize: 8,
          color: '#aaaaaa',
        });
        yOffset += 12;

        // Show each enemy on separate line, truncated if needed
        const uniqueEnemies = new Map<string, number>();
        this.state.enemies.forEach((e) => {
          uniqueEnemies.set(e.name, (uniqueEnemies.get(e.name) || 0) + 1);
        });

        uniqueEnemies.forEach((count, name) => {
          if (yOffset < windowY + windowHeight - 10) {
            const displayName = name.length > 12 ? name.substring(0, 11) + '.' : name;
            const text = count > 1 ? `${displayName} x${count}` : displayName;
            this.renderer.drawText(text, windowX + padding, yOffset, {
              font: 'ff1',
              fontSize: 7,
              color: '#cccccc',
            });
            yOffset += 10;
          }
        });
      }
    } else {
      // Defeat message - centered
      this.renderer.drawText('DEFEAT', windowX + windowWidth / 2, windowY + 20, {
        font: 'ff1',
        fontSize: 12,
        color: '#ff0000',
        align: 'center',
        shadow: true,
      });

      // Split defeat message into two lines for better fit
      this.renderer.drawText('Your party was', windowX + windowWidth / 2, windowY + 50, {
        font: 'ff1',
        fontSize: 8,
        color: '#ffffff',
        align: 'center',
      });

      this.renderer.drawText('wiped out...', windowX + windowWidth / 2, windowY + 62, {
        font: 'ff1',
        fontSize: 8,
        color: '#ffffff',
        align: 'center',
      });
    }
  }

  private renderDamageNumbers(ctx: CanvasRenderingContext2D): void {
    this.damageNumbers.forEach((dmg) => {
      const progress = dmg.elapsed / dmg.duration;
      const y = dmg.y - progress * 30; // Float upward
      const alpha = 1 - progress;

      ctx.save();
      ctx.globalAlpha = alpha;

      // Color: green for healing, yellow for crits, white for normal damage
      let color = '#ffffff';
      if (dmg.value < 0) {
        color = '#00ff00'; // Green for healing
      } else if (dmg.isCrit) {
        color = '#ffff00'; // Yellow for critical
      }

      const fontSize = dmg.isCrit ? 12 : 10;
      this.renderer.drawText(Math.abs(dmg.value).toString(), dmg.x, y, {
        font: 'ff1',
        fontSize,
        color,
        shadow: true,
      });

      ctx.restore();
    });
  }

  // =========================================================================
  // Input Handling
  // =========================================================================

  private handleMenuInput(input: GameInput): void {
    if (input.justPressed.has(GameKey.UP)) {
      this.menuIndex = Math.max(0, this.menuIndex - 1);
    } else if (input.justPressed.has(GameKey.DOWN)) {
      this.menuIndex = Math.min(this.battleMenuItems.length - 1, this.menuIndex + 1);
    } else if (input.justPressed.has(GameKey.A)) {
      // Confirm action
      const selectedItem = this.battleMenuItems[this.menuIndex];
      if (!selectedItem || selectedItem.enabled === false) return;

      this.selectedAction = selectedItem.value || 'fight';

      // Handle different actions
      if (this.selectedAction === 'fight') {
        this.state.phase = BattlePhase.TARGETING;
        this.selectedTarget = this.findFirstAliveEnemy();
      } else if (this.selectedAction === 'magic') {
        this.openMagicMenu();
      } else if (this.selectedAction === 'item') {
        this.openItemMenu();
      } else if (this.selectedAction === 'run') {
        this.attemptFlee();
      }
    } else if (input.justPressed.has(GameKey.B)) {
      // Cancel - go back to previous character
      if (this.selectedPartyMember > 0) {
        this.selectedPartyMember--;
      }
    }
  }

  private handleMagicMenuInput(input: GameInput): void {
    const character = this.party[this.selectedPartyMember];
    if (!character) return;

    const spellsByLevel = SpellSystem.getSpellsByLevel(character as any, character.learnedSpells);
    const levels = Array.from(spellsByLevel.keys()).sort();

    if (levels.length === 0) {
      // No spells, close menu
      if (input.justPressed.has(GameKey.B)) {
        this.magicMenuActive = false;
      }
      return;
    }

    const currentLevel = levels[this.magicLevelIndex] || 1;
    const spells = spellsByLevel.get(currentLevel) || [];

    if (input.justPressed.has(GameKey.UP)) {
      this.magicSpellIndex = Math.max(0, this.magicSpellIndex - 1);
    } else if (input.justPressed.has(GameKey.DOWN)) {
      this.magicSpellIndex = Math.min(spells.length - 1, this.magicSpellIndex + 1);
    } else if (input.justPressed.has(GameKey.LEFT)) {
      // Previous spell level
      this.magicLevelIndex = Math.max(0, this.magicLevelIndex - 1);
      this.magicSpellIndex = 0;
    } else if (input.justPressed.has(GameKey.RIGHT)) {
      // Next spell level
      this.magicLevelIndex = Math.min(levels.length - 1, this.magicLevelIndex + 1);
      this.magicSpellIndex = 0;
    } else if (input.justPressed.has(GameKey.A)) {
      // Select spell
      const spell = spells[this.magicSpellIndex];
      if (spell) {
        const charges = SpellSystem.getRemainingCharges(character as any, spell.id);
        if (charges > 0) {
          this.selectedSpell = spell;
          this.magicMenuActive = false;

          // Determine targeting based on spell
          if (this.requiresTargeting(spell)) {
            this.state.phase = BattlePhase.TARGETING;
            this.selectedTarget = this.getDefaultTarget(spell);
          } else {
            // Self-targeting or automatic, cast immediately
            this.executeMagic();
          }
        }
      }
    } else if (input.justPressed.has(GameKey.B)) {
      // Cancel - back to main menu
      this.magicMenuActive = false;
      this.selectedSpell = null;
    }
  }

  private handleTargetingInput(input: GameInput): void {
    const aliveEnemies = this.state.enemies.filter((e) => !e.dead);

    // Item targeting is different - targets party members
    if (this.selectedItem) {
      if (input.justPressed.has(GameKey.LEFT)) {
        this.selectedTarget = Math.max(0, this.selectedTarget - 1);
      } else if (input.justPressed.has(GameKey.RIGHT)) {
        this.selectedTarget = Math.min(this.party.length - 1, this.selectedTarget + 1);
      } else if (input.justPressed.has(GameKey.A)) {
        this.executeItem();
      } else if (input.justPressed.has(GameKey.B)) {
        // Go back to item menu
        this.selectedItem = null;
        this.itemMenuActive = true;
        this.state.phase = BattlePhase.MENU;
      }
      return;
    }

    if (input.justPressed.has(GameKey.LEFT)) {
      this.selectedTarget = Math.max(0, this.selectedTarget - 1);
    } else if (input.justPressed.has(GameKey.RIGHT)) {
      // Determine max based on spell target or default to enemies
      const maxTarget = this.selectedSpell
        ? this.getMaxTargetIndex(this.selectedSpell)
        : aliveEnemies.length - 1;

      this.selectedTarget = Math.min(maxTarget, this.selectedTarget + 1);
    } else if (input.justPressed.has(GameKey.A)) {
      // Execute action
      if (this.selectedSpell) {
        this.executeMagic();
      } else {
        this.executeAttack();
      }
    } else if (input.justPressed.has(GameKey.B)) {
      // Cancel
      if (this.selectedSpell) {
        // Go back to magic menu
        this.selectedSpell = null;
        this.magicMenuActive = true;
        this.state.phase = BattlePhase.MENU;
      } else {
        // Go back to main menu
        this.state.phase = BattlePhase.MENU;
      }
    }
  }

  // =========================================================================
  // Battle Logic
  // =========================================================================

  private executeAttack(): void {
    this.state.phase = BattlePhase.ANIMATING;

    const targetEnemy = this.state.enemies[this.selectedTarget];
    if (!targetEnemy) return;

    const attacker = this.party[this.selectedPartyMember];
    const attackerName = attacker?.name || 'Hero';

    // Show battle message
    this.showBattleMessage(`${attackerName} attacks!`);

    // Set attack pose
    this.characterPoses.set(this.selectedPartyMember, BattlePose.ATTACK);

    // Character steps forward
    this.effectsManager.characterStep(this.selectedPartyMember, true);

    // Delay for step animation
    setTimeout(() => {
      // Play slash effect at enemy position
      this.effectsManager.playEffect(
        EffectType.SLASH,
        targetEnemy.x + 16,
        targetEnemy.y + 16,
        {},
        () => {
          // Step back after attack and reset pose to idle
          this.effectsManager.characterStep(this.selectedPartyMember, false);
          setTimeout(() => {
            this.characterPoses.set(this.selectedPartyMember, BattlePose.IDLE);
          }, 200);
        }
      );

      // Check for miss
      const hitChance = 0.85; // 85% hit rate
      const isHit = Math.random() < hitChance;

      if (!isHit) {
        // Miss
        this.showBattleMessage(`${attackerName} missed!`);
        this.effectsManager.playEffect(EffectType.MISS, targetEnemy.x + 16, targetEnemy.y);
        return;
      }

      // TODO: This would actually call smart contract
      // For now, simulate damage
      const baseDamage = Math.floor(Math.random() * 50) + 10;
      const isCrit = Math.random() < 0.15; // 15% crit chance

      if (isCrit) {
        this.effectsManager.playEffect(EffectType.CRITICAL, targetEnemy.x + 16, targetEnemy.y - 10);
      }

      this.dealDamage(this.selectedTarget, baseDamage, isCrit);
    }, 150); // Wait for step forward
  }

  private executeMagic(): void {
    if (!this.selectedSpell) return;

    const caster = this.party[this.selectedPartyMember];
    if (!caster) return;

    this.state.phase = BattlePhase.ANIMATING;

    // Set cast pose
    this.characterPoses.set(this.selectedPartyMember, BattlePose.CAST);

    // Get targets based on spell
    const targets = this.getSpellTargets(this.selectedSpell);

    // Convert party/enemies to spell system format
    const spellCaster = this.convertToSpellCharacter(caster);
    const spellTargets = targets.map((t) =>
      'class' in t ? this.convertToSpellCharacter(t) : this.convertToSpellEnemy(t)
    );

    // Cast spell
    const result = SpellSystem.castSpell(
      this.selectedSpell.id,
      spellCaster,
      spellTargets,
      this.party.map((p) => this.convertToSpellCharacter(p)),
      this.state.enemies.map((e) => this.convertToSpellEnemy(e))
    );

    // Play spell animation
    this.playSpellAnimation(this.selectedSpell, targets);

    // Apply spell effects
    if (result.success && result.hit) {
      if (result.healing > 0) {
        // Show healing
        targets.forEach((target) => {
          if ('hp' in target) {
            const x = 'x' in target ? target.x + 16 : 120;
            const y = 'y' in target ? target.y : 100;
            this.effectsManager.showDamageNumber(x, y, result.healing, false, true); // true = healing
          }
        });
      } else if (result.damage > 0) {
        // Show damage
        targets.forEach((target) => {
          if ('x' in target && 'y' in target) {
            const enemyIndex = this.state.enemies.findIndex((e) => e.id === target.id);
            if (enemyIndex >= 0) {
              this.dealDamage(enemyIndex, result.damage, false);
            }
          }
        });
      }
    }

    // Update caster's charges (sync back)
    caster.spellCharges = spellCaster.spellCharges;

    // Reset pose to idle after a delay
    setTimeout(() => {
      this.characterPoses.set(this.selectedPartyMember, BattlePose.IDLE);
    }, 500);

    // Clear selected spell
    this.selectedSpell = null;
  }

  private dealDamage(targetIndex: number, damage: number, isCrit: boolean = false): void {
    const enemy = this.state.enemies[targetIndex];
    if (!enemy || enemy.dead) return;

    const finalDamage = isCrit ? Math.floor(damage * 1.5) : damage;
    enemy.hp = Math.max(0, enemy.hp - finalDamage);
    this.state.enemyHP[targetIndex] = enemy.hp;

    // Show damage number using effects manager
    this.effectsManager.showDamageNumber(enemy.x + 16, enemy.y, finalDamage, isCrit, false);

    // Enemy shake when hit
    this.effectsManager.enemyShake(enemy.x, enemy.y);

    // Flash entity
    this.effectsManager.flashEntity(enemy.x, enemy.y, 32, 32);

    // Screen shake for big hits
    this.effectsManager.screenShake(isCrit ? 10 : 6, isCrit ? 250 : 150);

    // Flash
    this.flashTimer = 100;

    // Check death
    if (enemy.hp <= 0) {
      enemy.dead = true;
      // Play death animation
      this.effectsManager.playEffect(EffectType.DEATH, enemy.x + 16, enemy.y + 16);
      this.checkVictory();
    }
  }

  private nextTurn(): void {
    this.state.currentTurn++;

    // Check if all party members have acted
    if (this.selectedPartyMember < this.party.length - 1) {
      // Skip dead party members
      this.selectedPartyMember++;
      while (this.selectedPartyMember < this.party.length) {
        const member = this.party[this.selectedPartyMember];
        if (member && member.hp > 0) break;
        this.selectedPartyMember++;
      }

      if (this.selectedPartyMember < this.party.length) {
        this.state.phase = BattlePhase.MENU;
        return;
      }
    }

    // All party members have acted - now it's enemy turn!
    this.executeEnemyTurn();
  }

  /**
   * Execute all enemy attacks in sequence
   */
  private executeEnemyTurn(): void {
    const aliveEnemies = this.state.enemies.filter(e => !e.dead);

    if (aliveEnemies.length === 0) {
      // No enemies left, check victory (shouldn't happen but safety check)
      this.checkVictory();
      return;
    }

    // Set enemy turn flag
    this.isEnemyTurn = true;
    console.log(`[BattleScene] Enemy turn - ${aliveEnemies.length} enemies attacking`);
    this.state.phase = BattlePhase.ANIMATING;

    // Execute enemy attacks sequentially with delays
    let attackDelay = 0;
    const ATTACK_INTERVAL = 800; // ms between attacks

    aliveEnemies.forEach((enemy, index) => {
      setTimeout(() => {
        this.executeEnemyAttack(enemy);

        // After last enemy attacks, check defeat and return to player
        if (index === aliveEnemies.length - 1) {
          setTimeout(() => {
            // Check if party is wiped
            if (this.checkDefeat()) {
              this.isEnemyTurn = false;
              return; // Defeat state set, don't continue
            }

            // Reset for next round - find first alive party member
            this.isEnemyTurn = false;
            this.selectedPartyMember = this.findFirstAlivePartyMember();
            this.state.phase = BattlePhase.MENU;
            this.showBattleMessage('Your turn!');
            console.log('[BattleScene] Player turn begins');
          }, 500);
        }
      }, attackDelay);

      attackDelay += ATTACK_INTERVAL;
    });
  }

  /**
   * Single enemy performs an attack on the party
   */
  private executeEnemyAttack(enemy: BattleEnemy): void {
    // Find a random alive party member to target
    const aliveParty = this.party
      .map((member, index) => ({ member, index }))
      .filter(({ member }) => member.hp > 0);

    if (aliveParty.length === 0) return;

    const targetInfo = aliveParty[Math.floor(Math.random() * aliveParty.length)];
    if (!targetInfo) return;

    const target = targetInfo.member;
    const targetIndex = targetInfo.index;

    // Show battle message
    this.showBattleMessage(`${enemy.name} attacks!`);
    console.log(`[BattleScene] ${enemy.name} attacks ${target.name}!`);

    // Hit check (enemies have ~80% hit rate)
    const hitChance = 0.80;
    const isHit = Math.random() < hitChance;

    if (!isHit) {
      // Miss - show miss text above party member
      this.showBattleMessage(`${enemy.name} missed!`);
      this.showPartyMemberMiss(targetIndex);
      console.log(`[BattleScene] ${enemy.name} missed ${target.name}!`);
      return;
    }

    // Calculate enemy damage
    // Formula: Base attack * random variance (0.8 - 1.2)
    const baseAttack = enemy.attack || 20;
    const variance = 0.8 + Math.random() * 0.4;
    const damage = Math.floor(baseAttack * variance);

    // Deal damage to party member
    this.dealDamageToParty(targetIndex, damage);
  }

  /**
   * Deal damage to a party member
   */
  private dealDamageToParty(targetIndex: number, damage: number): void {
    const member = this.party[targetIndex];
    if (!member) return;

    // Apply damage
    const oldHP = member.hp;
    member.hp = Math.max(0, member.hp - damage);
    this.state.partyHP[targetIndex] = member.hp;

    console.log(`[BattleScene] ${member.name} takes ${damage} damage! (${oldHP} -> ${member.hp})`);

    // Set hurt pose briefly
    this.characterPoses.set(targetIndex, BattlePose.HURT);
    setTimeout(() => {
      // Return to idle (or dead if HP is 0)
      if (member.hp <= 0) {
        this.characterPoses.set(targetIndex, BattlePose.DEAD);
      } else {
        this.characterPoses.set(targetIndex, BattlePose.IDLE);
      }
    }, 400);

    // Show damage number over party status window
    const windowX = 8;
    const windowY = 160;
    const x = windowX + 8 + targetIndex * 60 + 24; // Center over character slot
    const y = windowY + 8;

    this.damageNumbers.push({
      value: damage,
      x,
      y,
      elapsed: 0,
      duration: 1000,
      isCrit: false,
    });

    // Screen shake
    this.effectsManager.screenShake(4, 100);
    this.flashTimer = 50;

    // Check if character died
    if (member.hp <= 0) {
      member.status = 'DEAD';
      console.log(`[BattleScene] ${member.name} has fallen!`);
    }
  }

  /**
   * Show miss indicator for party member
   */
  private showPartyMemberMiss(targetIndex: number): void {
    const windowX = 8;
    const windowY = 160;
    const x = windowX + 8 + targetIndex * 60 + 24;
    const y = windowY + 8;

    // Use effects manager if it has a miss effect, otherwise just log
    this.effectsManager.playEffect(EffectType.MISS, x, y);
  }

  /**
   * Show a battle message (action feedback)
   */
  private showBattleMessage(message: string): void {
    this.battleMessage = message;
    this.messageTimer = this.MESSAGE_DURATION;
  }

  /**
   * Check if the entire party is dead
   */
  private checkDefeat(): boolean {
    const allDead = this.party.every(member => member.hp <= 0);

    if (allDead) {
      console.log('[BattleScene] Party defeated!');
      this.state.phase = BattlePhase.DEFEAT;
      this.resultTimer = 0;

      // Trigger callback after delay
      if (this.onBattleEndCallback) {
        setTimeout(() => {
          if (this.onBattleEndCallback) {
            const result: BattleResult = {
              victory: false,
              fled: false,
              expGained: 0,
              pasGained: 0,
              enemiesDefeated: [],
              updatedParty: [...this.party],
              updatedInventory: new Map(this.partyInventory),
            };
            this.onBattleEndCallback(result);
            this.onBattleEndCallback = null;
          }
        }, this.RESULT_DURATION);
      }

      return true;
    }

    return false;
  }

  /**
   * Find the first alive party member
   */
  private findFirstAlivePartyMember(): number {
    for (let i = 0; i < this.party.length; i++) {
      const member = this.party[i];
      if (member && member.hp > 0) {
        return i;
      }
    }
    return 0;
  }

  private checkVictory(): void {
    const allDead = this.state.enemies.every((e) => e.dead);
    if (allDead) {
      // Calculate rewards
      this.calculateRewards();

      // Distribute experience and check for level ups
      this.distributeExperience();

      // Play victory animation
      this.effectsManager.victoryPose();

      this.state.phase = BattlePhase.VICTORY;
    }
  }

  private calculateRewards(): void {
    let totalExp = 0;
    let totalPas = 0;

    // Calculate from defeated enemies
    this.state.enemies.forEach((enemy) => {
      const template = getEnemyTemplate(enemy.id);
      if (template) {
        totalExp += template.expReward;
        totalPas += template.gilReward; // Enemy templates use gilReward internally
      }
    });

    this.expGained = totalExp;
    this.pasGained = totalPas;

    console.log(`[BattleScene] Victory! Gained ${totalExp} EXP and ${totalPas} PAS`);
  }

  /**
   * Distribute experience to living party members and check for level ups
   */
  private distributeExperience(): void {
    if (this.expGained === 0) return;

    this.party.forEach((member, index) => {
      // Only living characters gain experience
      if (member.hp > 0) {
        // Create a temporary character object for level system
        const tempChar: Character = {
          class: member.class,
          name: member.name,
          level: member.level || 1,
          experience: (member.experience || 0) + this.expGained,
          strength: 0,
          agility: 0,
          intelligence: member.intelligence,
          vitality: 0,
          luck: 0,
          currentHP: member.hp,
          maxHP: member.maxHP,
          currentMP: member.mp,
          maxMP: member.maxMP,
          weapon: 0,
          armor: 0,
          helmet: 0,
          gloves: 0,
          accessory: 0,
          spellCharges: [],
          maxSpellCharges: [],
          knownSpells: [],
          status: 0,
        };

        // Add experience and check for level ups
        const levelUpResults = levelSystem.addExperience(tempChar, this.expGained);

        if (levelUpResults.length > 0) {
          this.levelUps.set(index, levelUpResults);

          // Update party member with new level and stats
          member.level = tempChar.level;
          member.experience = tempChar.experience;
          member.hp = tempChar.currentHP;
          member.maxHP = tempChar.maxHP;
          member.mp = tempChar.currentMP;
          member.maxMP = tempChar.maxMP;

          console.log(`[BattleScene] ${member.name} leveled up to ${tempChar.level}!`);
        }
      }
    });
  }

  // =========================================================================
  // Magic System Helper Methods
  // =========================================================================

  private openMagicMenu(): void {
    const character = this.party[this.selectedPartyMember];
    if (!character) return;

    // Check if character has any spells
    if (!character.learnedSpells || character.learnedSpells.length === 0) {
      console.log(`${character.name} has no magic!`);
      return;
    }

    this.magicMenuActive = true;
    this.magicLevelIndex = 0;
    this.magicSpellIndex = 0;
  }

  private requiresTargeting(spell: SpellDefinition): boolean {
    return spell.target === SpellTarget.SingleAlly || spell.target === SpellTarget.SingleEnemy;
  }

  private getDefaultTarget(spell: SpellDefinition): number {
    if (spell.target === SpellTarget.SingleEnemy) {
      return this.findFirstAliveEnemy();
    } else if (spell.target === SpellTarget.SingleAlly) {
      return 0; // Default to first party member
    }
    return 0;
  }

  private getMaxTargetIndex(spell: SpellDefinition): number {
    if (spell.target === SpellTarget.SingleEnemy) {
      return this.state.enemies.filter((e) => !e.dead).length - 1;
    } else if (spell.target === SpellTarget.SingleAlly) {
      return this.party.filter((p) => p.hp > 0).length - 1;
    }
    return 0;
  }

  private getSpellTargets(spell: SpellDefinition): (PartyMember | BattleEnemy)[] {
    switch (spell.target) {
      case SpellTarget.Self: {
        const self = this.party[this.selectedPartyMember];
        return self ? [self] : [];
      }

      case SpellTarget.SingleAlly: {
        const ally = this.party[this.selectedTarget];
        return ally ? [ally] : [];
      }

      case SpellTarget.AllAllies:
        return this.party.filter((p) => p.hp > 0 || spell.statusEffect === StatusEffect.Dead);

      case SpellTarget.SingleEnemy: {
        const enemy = this.state.enemies[this.selectedTarget];
        return enemy ? [enemy] : [];
      }

      case SpellTarget.AllEnemies:
        return this.state.enemies.filter((e) => !e.dead);

      default:
        return [];
    }
  }

  private playSpellAnimation(
    spell: SpellDefinition,
    targets: (PartyMember | BattleEnemy)[]
  ): void {
    const effectData = SpellSystem.getSpellEffect(spell.id);

    // Determine spell effect type based on element/type
    let effectType: EffectType = EffectType.HEAL;

    if (spell.name.toLowerCase().includes('fire')) {
      effectType = EffectType.FIRE;
    } else if (spell.name.toLowerCase().includes('ice') || spell.name.toLowerCase().includes('blizz')) {
      effectType = EffectType.ICE;
    } else if (spell.name.toLowerCase().includes('lit') || spell.name.toLowerCase().includes('bolt')) {
      effectType = EffectType.LIGHTNING;
    } else if (spell.statusEffect === StatusEffect.None && spell.power > 0) {
      // Offensive spell without specific element
      effectType = EffectType.LIGHTNING;
    } else if (spell.name.toLowerCase().includes('heal') || spell.name.toLowerCase().includes('cure')) {
      effectType = EffectType.HEAL;
    }

    // Flash effect
    this.flashTimer = effectData.duration;

    // Screen shake for powerful spells
    if (spell.power > 50) {
      this.effectsManager.screenShake(8, effectData.duration / 2);
    }

    // Play effect for each target
    targets.forEach((target) => {
      if ('x' in target && 'y' in target) {
        this.effectsManager.playEffect(effectType, target.x + 16, target.y + 16);

        const animation: BattleAnimation = {
          type: 'SPELL',
          target: target.id,
          elapsed: 0,
          duration: effectData.duration,
        };
        this.state.animations.push(animation);
      }
    });
  }

  private convertToSpellCharacter(member: PartyMember): SpellCharacter {
    return {
      id: member.id,
      name: member.name,
      class: member.class,
      level: member.level,
      hp: member.hp,
      maxHP: member.maxHP,
      mp: member.mp,
      maxMP: member.maxMP,
      intelligence: member.intelligence,
      spirit: member.spirit,
      status: this.parseStatus(member.status),
      spellCharges: member.spellCharges,
      elementalResistances: new Map(),
    };
  }

  private convertToSpellEnemy(enemy: BattleEnemy): SpellEnemy {
    return {
      id: enemy.id,
      name: enemy.name,
      hp: enemy.hp,
      maxHP: enemy.maxHP,
      magicDefense: 50, // Default
      elementalWeaknesses: new Map(),
      isUndead: false, // TODO: Get from enemy data
      status: [],
      dead: enemy.dead,
    };
  }

  private parseStatus(status: string): StatusEffect[] {
    const effects: StatusEffect[] = [];
    if (status.includes('DEAD')) effects.push(StatusEffect.Dead);
    if (status.includes('STONE')) effects.push(StatusEffect.Stone);
    if (status.includes('POISON')) effects.push(StatusEffect.Poison);
    if (status.includes('SILENCE')) effects.push(StatusEffect.Silence);
    if (status.includes('SLEEP')) effects.push(StatusEffect.Sleep);
    if (status.includes('PARALYZE')) effects.push(StatusEffect.Paralysis);
    if (status.includes('BLIND')) effects.push(StatusEffect.Blind);
    if (status.includes('CONFUSE')) effects.push(StatusEffect.Confuse);
    return effects;
  }

  // =========================================================================
  // Item System
  // =========================================================================

  /**
   * Set the party's inventory (called by Game.tsx when loading state)
   */
  setInventory(inventory: Map<number, number>): void {
    this.partyInventory = inventory;
  }

  /**
   * Open the item menu
   */
  private openItemMenu(): void {
    // Get list of usable consumable items
    const usableItems = this.getUsableItems();

    if (usableItems.length === 0) {
      this.showBattleMessage('No items!');
      return;
    }

    this.itemMenuActive = true;
    this.itemMenuIndex = 0;
    this.selectedItem = null;
  }

  /**
   * Get list of consumable items the party has
   */
  private getUsableItems(): { item: ItemDefinition; quantity: number }[] {
    const items: { item: ItemDefinition; quantity: number }[] = [];

    this.partyInventory.forEach((quantity, itemId) => {
      if (quantity > 0) {
        const item = getItem(itemId);
        // Only show consumable items in battle
        if (item && item.type === ItemType.CONSUMABLE) {
          items.push({ item, quantity });
        }
      }
    });

    return items;
  }

  /**
   * Render the item menu
   */
  private renderItemMenu(_ctx: CanvasRenderingContext2D): void {
    const menuX = 104;
    const menuY = 80;
    const menuWidth = 144;
    const menuHeight = 152;

    // Draw item menu window
    this.renderer.drawWindow(menuX, menuY, menuWidth, menuHeight);

    // Title
    this.renderer.drawText('ITEMS', menuX + menuWidth / 2, menuY + 8, {
      font: 'ff1',
      fontSize: 10,
      color: '#ffff00',
      align: 'center',
    });

    const usableItems = this.getUsableItems();
    let yOffset = menuY + 24;

    if (usableItems.length === 0) {
      this.renderer.drawText('No items', menuX + 8, yOffset, {
        font: 'ff1',
        fontSize: 8,
        color: '#808080',
      });
      return;
    }

    // Draw items
    usableItems.forEach(({ item, quantity }, index) => {
      const isSelected = index === this.itemMenuIndex;

      // Draw cursor
      if (isSelected) {
        this.renderer.drawCursor(menuX + 4, yOffset + 2);
      }

      // Draw item name
      this.renderer.drawText(item.name, menuX + 16, yOffset, {
        font: 'ff1',
        fontSize: 8,
        color: '#ffffff',
      });

      // Draw quantity
      this.renderer.drawText(`x${quantity}`, menuX + menuWidth - 32, yOffset, {
        font: 'ff1',
        fontSize: 8,
        color: '#00ff00',
      });

      yOffset += 12;
    });

    // Item description (if selected)
    const selectedItemData = usableItems[this.itemMenuIndex];
    if (selectedItemData) {
      const descY = menuY + menuHeight - 24;

      this.renderer.drawText(selectedItemData.item.description, menuX + 8, descY, {
        font: 'ff1',
        fontSize: 7,
        color: '#aaaaaa',
      });
    }
  }

  /**
   * Handle item menu input
   */
  private handleItemMenuInput(input: GameInput): void {
    const usableItems = this.getUsableItems();

    if (usableItems.length === 0) {
      // No items, close menu on any button
      if (input.justPressed.has(GameKey.B) || input.justPressed.has(GameKey.A)) {
        this.itemMenuActive = false;
      }
      return;
    }

    if (input.justPressed.has(GameKey.UP)) {
      this.itemMenuIndex = Math.max(0, this.itemMenuIndex - 1);
    } else if (input.justPressed.has(GameKey.DOWN)) {
      this.itemMenuIndex = Math.min(usableItems.length - 1, this.itemMenuIndex + 1);
    } else if (input.justPressed.has(GameKey.A)) {
      // Select item
      const selected = usableItems[this.itemMenuIndex];
      if (selected && selected.quantity > 0) {
        this.selectedItem = selected.item;
        this.itemMenuActive = false;

        // Items always target party members
        this.state.phase = BattlePhase.TARGETING;
        this.selectedTarget = this.findFirstAliveOrDeadPartyMember(selected.item);
      }
    } else if (input.justPressed.has(GameKey.B)) {
      // Cancel - back to main menu
      this.itemMenuActive = false;
      this.selectedItem = null;
    }
  }

  /**
   * Find appropriate party member target for item
   * Phoenix Down should target dead members, potions target alive
   */
  private findFirstAliveOrDeadPartyMember(item: ItemDefinition): number {
    // Phoenix Down targets dead members
    if (item.name === 'Phoenix Down') {
      for (let i = 0; i < this.party.length; i++) {
        const member = this.party[i];
        if (member && member.hp <= 0) return i;
      }
    }
    // Other items target alive members
    for (let i = 0; i < this.party.length; i++) {
      const member = this.party[i];
      if (member && member.hp > 0) return i;
    }
    return 0;
  }

  /**
   * Execute item use
   */
  private executeItem(): void {
    if (!this.selectedItem) return;

    const target = this.party[this.selectedTarget];
    if (!target) return;

    this.state.phase = BattlePhase.ANIMATING;
    const item = this.selectedItem;
    const caster = this.party[this.selectedPartyMember];
    const casterName = caster?.name || 'Hero';

    // Show battle message
    this.showBattleMessage(`${casterName} uses ${item.name}!`);

    // Use item based on type
    setTimeout(() => {
      let effectApplied = false;

      if (item.name === 'Potion' || item.name === 'Hi-Potion' || item.name === 'Elixir') {
        // Healing item
        const healAmount = item.name === 'Elixir' ? target.maxHP : item.power;
        const actualHeal = Math.min(healAmount, target.maxHP - target.hp);
        target.hp = Math.min(target.maxHP, target.hp + healAmount);

        // Show healing number
        const windowX = 8;
        const x = windowX + 30 + this.selectedTarget * 60;
        const y = 180;
        this.effectsManager.showDamageNumber(x, y, actualHeal, false, true);

        // Play heal effect
        this.effectsManager.playEffect(EffectType.HEAL, x, y);
        effectApplied = true;
      } else if (item.name === 'Phoenix Down') {
        // Revive
        if (target.hp <= 0) {
          target.hp = Math.floor(target.maxHP * (item.power / 100));
          target.status = 'OK';

          const windowX = 8;
          const x = windowX + 30 + this.selectedTarget * 60;
          const y = 180;
          this.effectsManager.showDamageNumber(x, y, target.hp, false, true);
          this.effectsManager.playEffect(EffectType.HEAL, x, y);
          effectApplied = true;
        }
      } else if (item.name === 'Ether') {
        // Restore spell charges (simplified: restore 1 charge to each spell)
        target.spellCharges.forEach((charges, spellId) => {
          const maxCharges = SpellSystem.getMaxCharges(spellId);
          target.spellCharges.set(spellId, Math.min(maxCharges, charges + 1));
        });
        effectApplied = true;
      } else if (item.spellEffect > 0) {
        // Status cure item (Antidote, Gold Needle, etc.)
        // Simplified: just reset status to OK
        target.status = 'OK';
        effectApplied = true;
      }

      // Consume item
      if (effectApplied) {
        const currentQty = this.partyInventory.get(item.id) || 0;
        if (currentQty > 0) {
          this.partyInventory.set(item.id, currentQty - 1);
        }
      }

      // Clear selected item and advance turn
      this.selectedItem = null;

      setTimeout(() => {
        this.nextTurn();
      }, 500);
    }, 300);
  }

  // =========================================================================
  // Animations
  // =========================================================================

  private _playAttackAnimation(targetIndex: number): void {
    // Simple flash animation
    const animation: BattleAnimation = {
      type: 'ATTACK',
      target: targetIndex,
      elapsed: 0,
      duration: 300,
    };

    this.state.animations.push(animation);
  }

  private updateAnimations(deltaTime: number): void {
    this.state.animations = this.state.animations.filter((anim) => {
      anim.elapsed += deltaTime;

      if (anim.elapsed >= anim.duration) {
        if (anim.onComplete) anim.onComplete();
        return false; // Remove completed
      }

      return true;
    });
  }

  private hasActiveAnimations(): boolean {
    return this.state.animations.length > 0;
  }

  private _showDamageNumber(
    value: number,
    x: number,
    y: number,
    isCrit: boolean = false
  ): void {
    this.damageNumbers.push({
      value,
      x,
      y,
      elapsed: 0,
      duration: 1000,
      isCrit,
    });
  }

  private findFirstAliveEnemy(): number {
    for (let i = 0; i < this.state.enemies.length; i++) {
      const enemy = this.state.enemies[i];
      if (enemy && !enemy.dead) {
        return i;
      }
    }
    return 0;
  }

  private updateDamageNumbers(deltaTime: number): void {
    this.damageNumbers = this.damageNumbers.filter((dmg) => {
      dmg.elapsed += deltaTime;
      return dmg.elapsed < dmg.duration;
    });
  }


  // =========================================================================
  // Public API
  // =========================================================================

  /**
   * Initialize battle with enemies
   */
  startBattle(enemies: BattleEnemy[], party: PartyMember[], zone: string = ''): void {
    this.encounterZone = zone;
    this.party = party;
    this.state.enemies = enemies;
    this.state.enemyHP = enemies.map((e) => e.hp);
    this.state.partyHP = party.map((p) => p.hp);
    this.state.phase = BattlePhase.INTRO;
    this.selectedPartyMember = 0;
    this.selectedTarget = 0;
    this.menuIndex = 0;
    this.introTimer = 0;

    // Position enemies (3 columns, centered)
    const enemyStartX = 120;
    const enemyStartY = 40;
    const spacing = 40;

    enemies.forEach((enemy, index) => {
      const col = index % 3;
      const row = Math.floor(index / 3);
      enemy.x = enemyStartX + col * spacing;
      enemy.y = enemyStartY + row * spacing;
    });
  }

  /**
   * Execute a battle action (for external control)
   */
  executeAction(action: {
    type: 'attack' | 'magic' | 'item' | 'run';
    actorIndex: number;
    targetIndex?: number;
    itemId?: number;
    spellId?: number;
  }): void {
    this.selectedPartyMember = action.actorIndex;
    if (action.targetIndex !== undefined) {
      this.selectedTarget = action.targetIndex;
    }
    this.selectedAction = action.type;

    // Execute based on type
    if (action.type === 'attack') {
      this.executeAttack();
    }
    // TODO: Handle magic, items, run
  }

  /**
   * End battle and transition
   */
  endBattle(victory: boolean): void {
    this.state.phase = victory ? BattlePhase.VICTORY : BattlePhase.DEFEAT;
    this.resultTimer = 0;
  }

  /**
   * Set party members
   */
  setParty(party: PartyMember[]): void {
    this.party = party;
    this.state.partyHP = party.map((p) => p.hp);
  }

  /**
   * Set enemies
   */
  setEnemies(enemies: BattleEnemy[]): void {
    this.state.enemies = enemies;
    this.state.enemyHP = enemies.map((e) => e.hp);
  }

  /**
   * Get current battle state
   */
  getState(): Readonly<BattleState> {
    return this.state;
  }

  /**
   * Check if battle is over
   */
  isBattleOver(): boolean {
    return this.state.phase === BattlePhase.VICTORY || this.state.phase === BattlePhase.DEFEAT;
  }

  /**
   * Get result (call after battle is over)
   */
  getResult(): BattleResult | null {
    if (!this.isBattleOver()) return null;

    return {
      victory: this.state.phase === BattlePhase.VICTORY,
      fled: false,
      expGained: this.expGained,
      pasGained: this.pasGained,
      enemiesDefeated: this.state.enemies.filter((e) => e.dead),
      levelUps: this.levelUps.size > 0 ? this.levelUps : undefined,
      updatedParty: [...this.party],
      updatedInventory: new Map(this.partyInventory),
    };
  }

  /**
   * Get level up results for display
   */
  getLevelUps(): Map<number, LevelUpResult[]> {
    return this.levelUps;
  }

  /**
   * Register callback for when battle ends
   */
  onBattleEnd(callback: (result: BattleResult) => void): void {
    this.onBattleEndCallback = callback;
  }

  /**
   * Attempt to flee from battle
   */
  attemptFlee(): boolean {
    // Simple flee chance (50% base)
    const fleeChance = 0.5;
    const success = Math.random() < fleeChance;

    if (success) {
      console.log('[BattleScene] Fled successfully!');
      // Trigger battle end callback with fled result
      if (this.onBattleEndCallback) {
        const result: BattleResult = {
          victory: false,
          fled: true,
          expGained: 0,
          pasGained: 0,
          enemiesDefeated: [],
          updatedParty: [...this.party],
          updatedInventory: new Map(this.partyInventory),
        };
        this.onBattleEndCallback(result);
      }
    } else {
      console.log('[BattleScene] Failed to flee!');
    }

    return success;
  }

  /**
   * Get encounter zone name
   */
  getEncounterZone(): string {
    return this.encounterZone;
  }
}
